"use client"

import { useState, useEffect, useCallback } from "react"
import { useRouter } from "next/navigation"
import { formatDistanceToNow } from "date-fns"
import { Bell, Check, ChevronRight, Trash2 } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { getNotificationUrl } from "@/lib/notification-url"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

interface Notification {
  id: string
  title?: string
  message: string
  type: string
  read_at: string | null
  created_at: string
  _source: "companion" | "user"
  companion?: { id?: string; name: string; avatar_url: string | null }
  companion_id?: string
  data?: Record<string, unknown>
}

const PAGE_SIZE = 20

type NotifTint = { bg: string; border: string; dot: string }

function getNotifTint(notif: { type: string; _source?: string; data?: Record<string, unknown> | null }): NotifTint {
  const type = notif.type ?? ""
  const d = notif.data ?? {}

  // Completed / paid — green (check before general commission so completions are green)
  if (
    type === "script_sale" ||
    type === "commission_completed" ||
    type === "commission_paid" ||
    type === "moderation_cleared" ||
    type.includes("purchase") ||
    type.includes("fulfilled") ||
    type.includes("delivered")
  ) {
    return { bg: "bg-green-500/[0.07]", border: "border-l-[3px] border-green-400/70", dot: "bg-green-400" }
  }

  // Commission in progress — amber/yellow
  if (
    type.startsWith("commission_") ||
    !!d.commission_id ||
    !!d.widget_commission_id ||
    !!d.widget_tracking_token
  ) {
    return { bg: "bg-amber-500/[0.07]", border: "border-l-[3px] border-amber-400/70", dot: "bg-amber-400" }
  }

  // Bounty — violet
  if (type.startsWith("bounty_") || !!d.bounty_id) {
    return { bg: "bg-violet-500/[0.07]", border: "border-l-[3px] border-violet-400/70", dot: "bg-violet-400" }
  }

  // Marketplace / script — orange
  if (type === "script_sale" || !!d.script_id || type === "content_flagged" || type === "content_theft") {
    return { bg: "bg-orange-500/[0.07]", border: "border-l-[3px] border-orange-400/70", dot: "bg-orange-400" }
  }

  // Social / followers — blue
  if (type === "new_followers") {
    return { bg: "bg-blue-500/[0.07]", border: "border-l-[3px] border-blue-400/70", dot: "bg-blue-400" }
  }

  // Moderation action against something the user created — red
  if (type === "moderation_warning" || type === "moderation_removal") {
    return { bg: "bg-red-500/[0.07]", border: "border-l-[3px] border-red-400/70", dot: "bg-red-400" }
  }

  // Companion / Goonpal messages — pink
  if (notif._source === "companion") {
    return { bg: "bg-pink-500/[0.07]", border: "border-l-[3px] border-pink-400/70", dot: "bg-pink-400" }
  }

  // Default
  return { bg: "", border: "", dot: "bg-primary" }
}

interface NotificationsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUnreadCountChange?: (count: number) => void
}

export function NotificationsModal({ open, onOpenChange, onUnreadCountChange }: NotificationsModalProps) {
  const t = useTranslations("notificationsModal")
  const router = useRouter()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [offset, setOffset] = useState(0)
  const [clearOpen, setClearOpen] = useState(false)
  const [clearing, setClearing] = useState(false)

  const fetchNotifications = useCallback(async (currentOffset: number, append = false) => {
    if (currentOffset === 0) setLoading(true)
    try {
      const res = await fetch(`/api/notifications?limit=${PAGE_SIZE}&offset=${currentOffset}`)
      const data = await res.json()
      if (data.notifications) {
        const fetched = data.notifications as Notification[]
        setNotifications(prev => {
          const next = append ? [...prev, ...fetched] : fetched
          onUnreadCountChange?.(next.filter(n => !n.read_at).length)
          return next
        })
        setHasMore(data.has_more ?? false)
        setOffset(currentOffset + fetched.length)
      }
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [onUnreadCountChange])

  useEffect(() => {
    if (!open) return
    setNotifications([])
    setOffset(0)
    setHasMore(false)
    fetchNotifications(0).then(() => {
      // Mark all as read after loading — opening the panel clears the indicator
      fetch("/api/notifications/mark-all-read", { method: "POST" }).catch(() => {})
      const now = new Date().toISOString()
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at ?? now })))
      onUnreadCountChange?.(0)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const markRead = async (id: string) => {
    await fetch(`/api/notifications/${id}/read`, { method: "POST" }).catch(() => {})
    setNotifications(prev => {
      const next = prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n)
      onUnreadCountChange?.(next.filter(n => !n.read_at).length)
      return next
    })
  }

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.read_at)
    await Promise.all(unread.map(n => fetch(`/api/notifications/${n.id}/read`, { method: "POST" }).catch(() => {})))
    setNotifications(prev => {
      const next = prev.map(n => ({ ...n, read_at: n.read_at ?? new Date().toISOString() }))
      onUnreadCountChange?.(0)
      return next
    })
  }

  const clearAll = async () => {
    setClearing(true)
    try {
      await fetch("/api/notifications", { method: "DELETE" })
      setNotifications([])
      setHasMore(false)
      onUnreadCountChange?.(0)
    } finally {
      setClearing(false)
      setClearOpen(false)
    }
  }

  const handleClick = async (notif: Notification) => {
    await markRead(notif.id)
    const url = getNotificationUrl(notif)
    if (url) {
      onOpenChange(false)
      router.push(url)
    }
  }

  const unreadCount = notifications.filter(n => !n.read_at).length

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-5 py-4 pr-14 border-b flex-row items-center justify-between space-y-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Bell className="h-4 w-4" />
            {t("notifications")}
            {unreadCount > 0 && (
              <Badge variant="destructive" className="h-4 px-1.5 text-[10px]">
                {unreadCount}
              </Badge>
            )}
          </DialogTitle>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="text-xs h-7 px-2 text-muted-foreground" onClick={markAllRead}>
                <Check className="h-3 w-3 mr-1" />
                {t("markAllRead")}
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                title={t("clearAllNotifications")}
                onClick={() => setClearOpen(true)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </DialogHeader>

        <ScrollArea className="h-[480px]">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Bell className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">{t("youReAllCaughtUp")}</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notif) => {
                const url = getNotificationUrl(notif)
                const { bg, border, dot } = getNotifTint(notif)
                return (
                  <button
                    type="button"
                    key={notif.id}
                    className={cn(
                      "w-full text-left px-5 py-3.5 transition-colors hover:brightness-[1.04]",
                      border,
                      bg || (!notif.read_at ? "bg-primary/5" : ""),
                      url ? "cursor-pointer" : "cursor-default"
                    )}
                    onClick={() => handleClick(notif)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        {notif.title && (
                          <p className="text-sm font-medium leading-snug mb-0.5">{notif.title}</p>
                        )}
                        <p className="text-sm text-muted-foreground leading-snug line-clamp-2">{notif.message}</p>
                        <p className="text-xs text-muted-foreground/60 mt-1">
                          {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0 mt-1">
                        {!notif.read_at && (
                          <span className={cn("h-2 w-2 rounded-full", dot)} />
                        )}
                        {url && (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
                        )}
                      </div>
                    </div>
                  </button>
                )
              })}

              {hasMore && (
                <div className="py-3 text-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={loadingMore}
                    onClick={() => {
                      setLoadingMore(true)
                      fetchNotifications(offset, true)
                    }}
                  >
                    {loadingMore ? t("loading") : t("loadMore")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>

    <AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("clearAllNotifications2")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("thisPermanentlyDeletesAllNotifications", { count: notifications.length })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={clearing}>{t("cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={clearAll} disabled={clearing} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            {clearing ? t("clearing") : t("clearAll")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  )
}
